import { FIXED_LIMITS, type ServerDto } from '@lagerfeuer/shared';
import { useMemo, useState, type FormEvent } from 'react';
import { ImageUpload } from '../../components/ImageUpload';
import { SaveBar, SettingsHeader } from '../../components/SettingsLayout';
import { ApiError, errorMessage } from '../../lib/api';
import { serversApi, type ServerPatch } from '../../lib/endpoints';
import { inviteUrl } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { toast } from '../../store/ui';
import { ServerIcon } from '../shell/ServerIcon';
import { useEdits } from './useEdits';

const VANITY_RE = /^[a-z0-9-]{3,32}$/;

const AFK_TIMEOUTS = [
  { value: 60, label: '1 Minute' },
  { value: 300, label: '5 Minuten' },
  { value: 900, label: '15 Minuten' },
  { value: 1800, label: '30 Minuten' },
  { value: 3600, label: '1 Stunde' },
];

type Editable = Required<ServerPatch>;

function applyServer(dto: ServerDto): void {
  useAppStore.getState().dispatch('SERVER_UPDATE', dto);
}

/** Tab „Übersicht": Name, Beschreibung, Icon/Banner, Vanity-URL, System-/AFK-Kanal, Standard-Benachrichtigungen. */
export function OverviewTab({ serverId }: { serverId: string }) {
  const server = useAppStore((s) => s.servers[serverId]);
  const channels = useAppStore((s) => s.channels);
  const maxBytes = useAppStore((s) => s.limits.maxAvatarBytes);
  const base = useMemo<Editable | undefined>(
    () =>
      server && {
        name: server.name,
        description: server.description,
        systemChannelId: server.systemChannelId,
        rulesChannelId: server.rulesChannelId,
        afkChannelId: server.afkChannelId,
        afkTimeoutSeconds: server.afkTimeoutSeconds,
        defaultNotifications: server.defaultNotifications,
      },
    [server],
  );
  const { value, patch, dirty, set, reset } = useEdits(base);
  const [busy, setBusy] = useState(false);
  const textChannels = useMemo(() => Object.values(channels).filter((c) => c.serverId === serverId && (c.type === 'TEXT' || c.type === 'ANNOUNCEMENT')).sort((a, b) => a.position - b.position), [channels, serverId]);
  const voiceChannels = useMemo(() => Object.values(channels).filter((c) => c.serverId === serverId && c.type === 'VOICE').sort((a, b) => a.position - b.position), [channels, serverId]);

  if (!server || !value) return null;

  const save = async () => {
    if (patch.name !== undefined && !patch.name.trim()) {
      toast.error('Der Servername darf nicht leer sein.');
      return;
    }
    setBusy(true);
    try {
      const body: ServerPatch = { ...patch };
      if (body.name !== undefined) body.name = body.name.trim();
      if (body.description !== undefined) body.description = body.description?.trim() || null;
      applyServer(await serversApi.update(serverId, body));
      reset();
      toast.success('Gespeichert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen', errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack settings-stack">
      <SettingsHeader title="Server-Übersicht" />
      <div className="overview-media">
        <ImageUpload
          label="Server-Icon"
          url={server.iconUrl}
          maxBytes={maxBytes}
          placeholder={<ServerIcon server={server} size={84} />}
          onUpload={async (f) => applyServer(await serversApi.setIcon(serverId, f))}
          onRemove={async () => applyServer(await serversApi.removeIcon(serverId))}
          testId="server-icon-upload"
        />
        <ImageUpload
          label="Banner"
          url={server.bannerUrl}
          shape="banner"
          maxBytes={maxBytes}
          hint="Erscheint über der Kanalliste und auf Einladungen. Empfohlen 16:9; GIF/WebP dürfen animiert sein."
          onUpload={async (f) => applyServer(await serversApi.setBanner(serverId, f))}
          onRemove={async () => applyServer(await serversApi.removeBanner(serverId))}
          testId="server-banner-upload"
        />
      </div>
      <div className="field">
        <label htmlFor="server-name">Servername</label>
        <input id="server-name" className="input" value={value.name} maxLength={FIXED_LIMITS.serverNameMax} onChange={(e) => set('name', e.target.value)} data-testid="server-name" />
      </div>
      <div className="field">
        <label htmlFor="server-description">Beschreibung</label>
        <textarea
          id="server-description"
          className="textarea"
          rows={3}
          maxLength={1000}
          value={value.description ?? ''}
          onChange={(e) => set('description', e.target.value || null)}
          data-testid="server-description"
        />
        <span className="field-hint">Wird in Einladungen angezeigt.</span>
      </div>

      <VanityField serverId={serverId} current={server.vanityCode} />

      <h2 className="settings-subheading">Systemkanäle</h2>
      <div className="field">
        <label htmlFor="server-system-channel">Kanal für Systemnachrichten</label>
        <select id="server-system-channel" className="select" value={value.systemChannelId ?? ''} onChange={(e) => set('systemChannelId', e.target.value || null)} data-testid="server-system-channel">
          <option value="">Keine Systemnachrichten</option>
          {textChannels.map((c) => (
            <option key={c.id} value={c.id}>
              # {c.name}
            </option>
          ))}
        </select>
        <span className="field-hint">Hier begrüßt Lagerfeuer neue Mitglieder.</span>
      </div>
      <div className="field">
        <label htmlFor="server-rules-channel">Regelkanal</label>
        <select id="server-rules-channel" className="select" value={value.rulesChannelId ?? ''} onChange={(e) => set('rulesChannelId', e.target.value || null)} data-testid="server-rules-channel">
          <option value="">Kein Regelkanal</option>
          {textChannels.map((c) => (
            <option key={c.id} value={c.id}>
              # {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div className="field" style={{ flex: 2, minWidth: 200 }}>
          <label htmlFor="server-afk-channel">AFK-Kanal</label>
          <select id="server-afk-channel" className="select" value={value.afkChannelId ?? ''} onChange={(e) => set('afkChannelId', e.target.value || null)} data-testid="server-afk-channel">
            <option value="">Kein AFK-Kanal</option>
            {voiceChannels.map((c) => (
              <option key={c.id} value={c.id}>
                🔊 {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ flex: 1, minWidth: 160 }}>
          <label htmlFor="server-afk-timeout">Inaktiv nach</label>
          <select
            id="server-afk-timeout"
            className="select"
            value={value.afkTimeoutSeconds}
            onChange={(e) => set('afkTimeoutSeconds', Number(e.target.value))}
            disabled={!value.afkChannelId}
            data-testid="server-afk-timeout"
          >
            {AFK_TIMEOUTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <h2 className="settings-subheading">Standard-Benachrichtigungen</h2>
      <fieldset className="radio-group" data-testid="server-default-notifications">
        <legend className="sr-only">Standard-Benachrichtigungen</legend>
        <label className="radio-card">
          <input type="radio" name="default-notifications" checked={value.defaultNotifications === 'ALL'} onChange={() => set('defaultNotifications', 'ALL')} data-testid="server-notify-all" />
          <span>
            <strong>Alle Nachrichten</strong>
            <span className="muted small"> – Mitglieder werden bei jeder Nachricht benachrichtigt.</span>
          </span>
        </label>
        <label className="radio-card">
          <input type="radio" name="default-notifications" checked={value.defaultNotifications === 'MENTIONS'} onChange={() => set('defaultNotifications', 'MENTIONS')} data-testid="server-notify-mentions" />
          <span>
            <strong>Nur @Erwähnungen</strong>
            <span className="muted small"> – empfohlen für größere Server.</span>
          </span>
        </label>
      </fieldset>

      <SaveBar dirty={dirty} busy={busy} onSave={() => void save()} onReset={reset} testId="server-overview-save" />
    </div>
  );
}

function VanityField({ serverId, current }: { serverId: string; current: string | null }) {
  const [code, setCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = code ?? current ?? '';
  const changed = code !== null && code !== (current ?? '');
  const valid = shown === '' || VANITY_RE.test(shown);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!changed || !valid) return;
    setBusy(true);
    setError(null);
    try {
      const res = await serversApi.setVanity(serverId, shown || null);
      const s = useAppStore.getState().servers[serverId];
      if (s) {
        const { me: _me, ...dto } = s;
        applyServer({ ...dto, vanityCode: res.code });
      }
      setCode(null);
      toast.success(res.code ? 'Vanity-URL gespeichert' : 'Vanity-URL entfernt');
    } catch (err) {
      setError(err instanceof ApiError && err.code === 'VANITY_TAKEN' ? 'Diese Vanity-URL ist schon vergeben.' : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="field" onSubmit={submit} data-testid="server-vanity">
      <label htmlFor="server-vanity-input">Vanity-URL</label>
      <div className="vanity-row">
        <span className="vanity-prefix" aria-hidden="true">
          {inviteUrl('')}
        </span>
        <input
          id="server-vanity-input"
          className="input"
          value={shown}
          placeholder="mein-lagerfeuer"
          maxLength={32}
          onChange={(e) => setCode(e.target.value.toLowerCase())}
          aria-invalid={!valid}
          data-testid="server-vanity-input"
        />
        <button className="btn btn-primary" disabled={!changed || !valid || busy} data-testid="server-vanity-save">
          {shown ? 'Speichern' : 'Entfernen'}
        </button>
      </div>
      <span className="field-hint">3–32 Zeichen: Kleinbuchstaben, Ziffern und Bindestriche. Leer lassen und speichern entfernt die Vanity-URL.</span>
      {(!valid || error) && (
        <span className="error-text" role="alert" data-testid="server-vanity-error">
          {error ?? 'Nur a–z, 0–9 und „-", 3 bis 32 Zeichen.'}
        </span>
      )}
    </form>
  );
}
